import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, ResolveFn, Router, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@tolgee/ngx';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { NAV_ROUTES } from '../app.routes.constant';
import { SearchIndex } from '../feature/dashboard-preview/services/search-index-builder.service';
import { DashboardV4 } from '../public/dashboard/shared/model/dashboard-v4';
import { DashboardV5CoreModule } from '../public/dashboard/store/dashboard-v5';
import { BackendFeatureFlagsService } from '../services/backend/backend-feature-flags.service';
import { PublishedBoardsFacadeService } from '../services/store/published-boards/published-boards-facade.service';
import { PublishedBoardBuilderService } from '../standalone/components/v4-self-contained-dashboard/published-board-builder.service';

export const publicVersionMatcher: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const boardId = route.params.boardId;
  const publishedBoardsFacade = inject(PublishedBoardsFacadeService);
  const router = inject(Router);
  return publishedBoardsFacade.getPublishedBoard$(boardId).pipe(
    map((published) => {
      if (published instanceof Error || (published instanceof HttpErrorResponse && published.status === 404)) {
        if (state.url === '/public/board/' + boardId) {
          return true;
        }
        return router.parseUrl(NAV_ROUTES.PUBLIC_LINK(boardId));
      }
      switch (published.version) {
        case 'V4':
        case 'V4.1':
        case 'V4.2':
          if (state.url.split('/').includes('v4')) {
            return true;
          }
          return router.parseUrl(NAV_ROUTES.V4_SELF_CONTAINED(boardId));
        case 'V5':
        case 'V5.1':
          if (state.url.startsWith('/public/board/' + boardId)) {
            return true;
          }
          return router.parseUrl(NAV_ROUTES.PUBLIC_LINK(boardId));
        case 'V3':
        case 'V3.1':
        case 'V3.2':
          if (state.url.includes('v3')) {
            return true;
          }
          return router.parseUrl(NAV_ROUTES.V3_SELF_CONTAINED(boardId));
        default:
          if (state.url.startsWith('/public/board/' + boardId)) {
            return true;
          }
          return router.parseUrl(NAV_ROUTES.PUBLIC_LINK(boardId));
      }
    }),
    catchError(() => {
      if (state.url.startsWith('/public/board/' + boardId)) {
        return of(true);
      }
      return of(router.parseUrl(NAV_ROUTES.PUBLIC_LINK(boardId)));
    })
  );
};

export enum ComponentToCreate {
  NOT_FOUND = 'NOT_FOUND',
  DASHBOARD_V5 = 'DASHBOARD_V5',
}

export const publicVersionComponentResolver: ResolveFn<{
  component: ComponentToCreate;
  additionalData: DashboardV4 | DashboardV5CoreModule | null;
  search?: SearchIndex[];
  publishedAt?: string;
}> = (route: ActivatedRouteSnapshot) => {
  const boardId = route.params.boardId || route.data.boardId;
  const publishedBoardsFacade = inject(PublishedBoardsFacadeService);
  const publishedBoardBuilder = inject(PublishedBoardBuilderService);
  const featureFlagService = inject(BackendFeatureFlagsService);
  const _translateService = inject(TranslateService);
  return forkJoin([
    featureFlagService.loadFlagsForEditorOrPublicRoute(boardId),
    publishedBoardsFacade.getPublishedBoard$(boardId),
  ]).pipe(
    switchMap(([_, published]) => {
      if (published instanceof Error) {
        return of({ component: ComponentToCreate.NOT_FOUND, additionalData: null });
      }
      _translateService.tolgee.addActiveNs('dashboard');
      if (Number(published.version.replace('V', '')) >= 5) {
        return forkJoin({
          core: publishedBoardBuilder.buildDashboardV5CoreModule(boardId),
          search: publishedBoardBuilder.buildDashboardV5SearchModule(boardId),
        }).pipe(
          map(({ core, search }) => ({
            component: ComponentToCreate.DASHBOARD_V5,
            additionalData: core,
            search,
            publishedAt: published.date,
          }))
        );
      }
      return of({ component: ComponentToCreate.NOT_FOUND, additionalData: null });
    }),
    catchError(() => {
      return of({ component: ComponentToCreate.NOT_FOUND, additionalData: null });
    })
  );
};
